/**
 * The Nexus as the Map app draws it: the hub's bounds, its six districts and the landmarks worth a pin, in
 * ANCHOR SPACE — block coordinates relative to the emblem's centre, exactly as the mod's own
 * `fabric/.../nexus/layout.json` writes them (`scripts/islands/spawn/ship.py`). North is −z.
 *
 * A COPY, HELD TO ITS SOURCE: the layout is ~600 KB of pieces, portal blocks and emitters a phone has no use
 * for, so the few numbers the map needs are written out here, and `nexusLayout.test.ts` reads the real file
 * and fails the build the moment they differ. The picture under them, `assets/nexus-town-map.png`, is
 * rendered from the same shipped pieces by `scripts/islands/spawn/town_map.py`, three pixels a block from
 * the bounds' minimum corner, so an SVG viewBox in block units lays one exactly over the other.
 */

/** The Nexus's world (the mod's `AudioConfig.NEXUS`): the only dimension the town map is a map of. */
export const NEXUS_DIMENSION = 'cobblemondimensions:nexus';

export interface BlockBox {
  readonly minX: number;
  readonly minZ: number;
  readonly maxX: number;
  readonly maxZ: number;
}

export interface District extends BlockBox {
  /** The id the mod sends as `context.district`. */
  readonly id: string;
  /** The name to show when the catalog does not carry one (the mod's shipped area names). */
  readonly name: string;
}

export interface Landmark {
  readonly id: string;
  readonly name: string;
  readonly x: number;
  readonly z: number;
}

/** `layout.json`'s `bounds`, x and z only. */
export const NEXUS_BOUNDS: BlockBox = { minX: -111, minZ: -108, maxX: 113, maxZ: 111 };

/** Pixels per block in the rendered town map (`town_map.py`'s SCALE). */
export const TOWN_MAP_SCALE = 3;

/** `layout.json`'s `districts`, in its order, with the names the mod's shipped `audio.yml` gives them. */
export const NEXUS_DISTRICTS: readonly District[] = [
  { id: 'battle_isle', name: 'Battle Isle', minX: 4, minZ: 39, maxX: 109, maxZ: 108 },
  { id: 'blossom_gardens', name: 'Blossom Gardens', minX: -106, minZ: -61, maxX: -41, maxZ: 36 },
  { id: 'gateway', name: 'The Gateway', minX: -31, minZ: -40, maxX: 31, maxZ: 28 },
  { id: 'observatory_isle', name: 'Observatory Isle', minX: -22, minZ: -103, maxX: 23, maxZ: -54 },
  { id: 'trainers_row', name: "Trainers' Row", minX: 35, minZ: -89, maxX: 112, maxZ: 35 },
  { id: 'wild_isle', name: 'Wild Isle', minX: -100, minZ: 37, maxX: -10, maxZ: 110 },
];

/** A pin per place a newcomer asks for, at the label point `layout.json` hangs over it (`labels[].pos`). */
export const NEXUS_LANDMARKS: readonly Landmark[] = [
  { id: 'gate', name: 'Dimension Gate', x: 0.5, z: -20.5 },
  { id: 'observatory', name: 'Observatory', x: 0.5, z: -66.5 },
  { id: 'trainer_center', name: 'Trainer Center', x: 82.5, z: 0.5 },
  { id: 'mart', name: 'Mart & Post', x: 59.5, z: -1.5 },
  { id: 'arena', name: 'Arena', x: 46.5, z: 55.5 },
  { id: 'battle_tower', name: 'Battle Tower', x: 82.5, z: 52.5 },
  { id: 'wonder_trade', name: 'Wonder Trade', x: -59.5, z: 0.5 },
  { id: 'hunters_lodge', name: "Hunters' Lodge", x: -45.5, z: 67.5 },
  { id: 'leap', name: 'Leap Point', x: -36.5, z: 17.5 },
];

export function districtById(id: string | undefined | null): District | null {
  if (id === undefined || id === null) return null;
  return NEXUS_DISTRICTS.find((district) => district.id === id) ?? null;
}

/** A box's SVG rectangle in block units: a box's max is inclusive, so it is one block wider than max − min. */
export function boxRect(box: BlockBox): { readonly x: number; readonly y: number; readonly width: number; readonly height: number } {
  return { x: box.minX, y: box.minZ, width: box.maxX - box.minX + 1, height: box.maxZ - box.minZ + 1 };
}

/** Where a box's label goes: its centre, in block units. */
export function boxCentre(box: BlockBox): { readonly x: number; readonly y: number } {
  return { x: (box.minX + box.maxX + 1) / 2, y: (box.minZ + box.maxZ + 1) / 2 };
}
